import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Loader2, Download } from 'lucide-react';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { useAuth } from '../contexts/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function statusBadge(status) {
  if (!status) return <Badge variant="outline">—</Badge>;
  if (status === 'sent' || status === 'dispatched') {
    return <Badge className="bg-green-100 text-green-700 border-green-200">sent</Badge>;
  }
  if (status === 'bounced') {
    return <Badge className="bg-red-100 text-red-700 border-red-200">bounced</Badge>;
  }
  if (status === 'delayed') {
    return <Badge className="bg-amber-100 text-amber-700 border-amber-200">delayed</Badge>;
  }
  return <Badge variant="outline">{status}</Badge>;
}

function PendingAckCard({ recipient, reference }) {
  const COOLDOWN_MS = 10 * 60 * 1000;
  const lastResent = recipient.ack_resend_last_at ? new Date(recipient.ack_resend_last_at) : null;
  const inCooldown = lastResent && (Date.now() - lastResent.getTime()) < COOLDOWN_MS;

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [coolingDown, setCoolingDown] = useState(!!inCooldown);
  const { token } = useAuth();

  async function handleResend() {
    if (sending || sent || coolingDown) return;
    setSending(true);
    try {
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      await axios.post(
        `${API_URL}/api/admin/document-delivery/logs/${reference}/resend-ack`,
        { recipient_email: recipient.email },
        { withCredentials: true, headers },
      );
      setSent(true);
      setCoolingDown(true);
      toast.success(`Acknowledgement reminder sent to ${recipient.email}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setCoolingDown(true);
        toast.warning(detail || 'A reminder was already sent recently. Please wait before resending.');
      } else {
        toast.error(detail || 'Failed to resend acknowledgement email');
      }
    } finally {
      setSending(false);
    }
  }

  const isDisabled = sending || sent || coolingDown;
  return (
    <div
      onClick={!isDisabled ? handleResend : undefined}
      className={`border rounded p-2 text-xs transition-colors ${!isDisabled ? 'cursor-pointer hover:bg-muted' : 'cursor-default opacity-60'}`}
      title={
        coolingDown ? 'A reminder was sent recently — please wait before resending'
        : sent ? 'Reminder sent'
        : 'Click to resend acknowledgement email'
      }
    >
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-gray-500 text-[10px]">
          {sending ? 'Sending…' : sent ? 'Sent ✓' : coolingDown ? 'Sent recently' : 'Pending'}
        </Badge>
        <span className="font-medium">{recipient.name}</span>
        <span className="text-muted-foreground">{recipient.email}</span>
      </div>
    </div>
  );
}

export const AdminDocumentDeliveryTab = ({ onTotalChange }) => {
  const {
    publicDocumentDispatchEnabled,
    refreshSiteSettings,
  } = useSiteSettings();
  const [stats, setStats] = useState(null);
  const [templateStats, setTemplateStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSender, setFilterSender] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dispatchVisibilityEnabled, setDispatchVisibilityEnabled] = useState(publicDocumentDispatchEnabled);
  const [updatingDispatchVisibility, setUpdatingDispatchVisibility] = useState(false);

  const LIMIT = 50;

  useEffect(() => {
    setDispatchVisibilityEnabled(publicDocumentDispatchEnabled);
  }, [publicDocumentDispatchEnabled]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/document-delivery/stats`, {
        withCredentials: true,
      });
      setStats(res.data);
    } catch (_) {}
  }, []);

  const fetchTemplateStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/document-delivery/manual-template-stats`, {
        withCredentials: true,
      });
      setTemplateStats(res.data);
    } catch (_) {}
  }, []);

  const fetchLogs = useCallback(async (targetPage = 1) => {
    setLoading(true);
    try {
      const params = { page: targetPage, limit: LIMIT };
      if (filterStatus) params.status = filterStatus;
      if (filterSender.trim()) params.sender_email = filterSender.trim();
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      const res = await axios.get(`${API_URL}/api/admin/document-delivery/logs`, {
        withCredentials: true,
        params,
      });
      setLogs(res.data.results || []);
      const newTotal = res.data.total || 0;
      setTotal(newTotal);
      onTotalChange?.(newTotal);
      setPage(targetPage);
    } catch (_) {
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSender, filterDateFrom, filterDateTo, onTotalChange]);

  useEffect(() => {
    fetchStats();
    fetchTemplateStats();
    fetchLogs(1);
  }, [fetchStats, fetchTemplateStats, fetchLogs]);

  const handleDownloadReceipt = async (e, reference) => {
    e.stopPropagation();
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/document-delivery/logs/${encodeURIComponent(reference)}/receipt.pdf`,
        { withCredentials: true, responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `delivery-record-${reference}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error('Failed to download receipt');
    }
  };

  const handleRowClick = async (reference) => {
    setSelectedLog(null);
    setDetailLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/document-delivery/logs/${encodeURIComponent(reference)}`,
        { withCredentials: true },
      );
      setSelectedLog(res.data);
    } catch (_) {
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!logs.length) return;
    const cols = [
      'reference', 'document_type', 'declared_sender_email', 'declared_sender_name',
      'property_address', 'recipients', 'sent_at', 'delivery_status',
      'ip_country', 'ip_city', 'abuse_score', 'is_suspicious',
      'tos_version_accepted', 'tos_accepted_at',
    ];
    const dataRows = logs.map((log) =>
      cols.map((c) => {
        if (c === 'recipients') {
          return `"${(log.recipients || []).map((r) => `${r.name} <${r.email}>`).join('; ')}"`;
        }
        const v = log[c] != null ? String(log[c]) : '';
        return `"${v.replace(/"/g, '""')}"`;
      }).join(','),
    );
    const csv = [cols.join(','), ...dataRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `document_delivery_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const bounceRate =
    stats && stats.total_deliveries > 0
      ? `${((stats.by_status.bounced / stats.total_deliveries) * 100).toFixed(1)}%`
      : '0%';

  const handleDispatchVisibilityChange = async (checked) => {
    const previousValue = dispatchVisibilityEnabled;
    setDispatchVisibilityEnabled(checked);
    setUpdatingDispatchVisibility(true);

    try {
      await axios.patch(
        `${API_URL}/api/admin/site-settings/document-dispatch`,
        { public_document_dispatch_enabled: checked },
        { withCredentials: true },
      );
      await refreshSiteSettings();
      toast.success(
        checked
          ? 'Public document delivery is now visible in the public site.'
          : 'Public document delivery is now limited to registered landlords.',
      );
    } catch (error) {
      setDispatchVisibilityEnabled(previousValue);
      toast.error(error.response?.data?.detail || 'Failed to update document delivery visibility');
    } finally {
      setUpdatingDispatchVisibility(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-navy-900">Public Send Documents page</p>
              <p className="text-sm text-muted-foreground">
                Turn this off to remove the public navbar link and restrict the page to authenticated landlords only.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant={dispatchVisibilityEnabled ? 'default' : 'outline'} className={dispatchVisibilityEnabled ? 'bg-green-600' : ''}>
                {dispatchVisibilityEnabled ? 'Public' : 'Landlords only'}
              </Badge>
              <Switch
                checked={dispatchVisibilityEnabled}
                onCheckedChange={handleDispatchVisibilityChange}
                disabled={updatingDispatchVisibility}
                aria-label="Toggle public Send Documents visibility"
              />
              {updatingDispatchVisibility ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold">{stats?.total_deliveries ?? '—'}</p>
            <p className="text-xs text-muted-foreground">Total deliveries</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold">{stats?.deliveries_today ?? '—'}</p>
            <p className="text-xs text-muted-foreground">Deliveries today</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold">{bounceRate}</p>
            <p className="text-xs text-muted-foreground">Bounce rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Manual template download counter + chart */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <p className="text-sm font-semibold text-navy-900">Manual Template Downloads</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
            <span>Total: <span className="font-medium text-foreground">{templateStats?.total_downloads ?? '—'}</span></span>
            <span>Last download: <span className="font-medium text-foreground">
              {templateStats?.last_downloaded_at
                ? new Date(templateStats.last_downloaded_at).toLocaleString()
                : '—'}
            </span></span>
          </div>
          {templateStats?.daily_downloads && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Downloads per day (last 14 days)</p>
              <ResponsiveContainer width="100%" height={80}>
                <BarChart data={templateStats.daily_downloads} margin={{ top: 0, right: 4, left: -28, bottom: 0 }}>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 9 }}
                    tickFormatter={(d) => {
                      const [, , day] = d.split('-');
                      return day;
                    }}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                  <ChartTooltip
                    formatter={(val) => [val, 'Downloads']}
                    labelFormatter={(label) => {
                      const d = new Date(label + 'T00:00:00Z');
                      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                    }}
                  />
                  <Bar dataKey="count" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <select
              className="rounded-md border px-3 py-2 text-sm bg-background"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="sent">Sent</option>
              <option value="bounced">Bounced</option>
              <option value="delayed">Delayed</option>
              <option value="complained">Complained</option>
            </select>
            <Input
              placeholder="Sender email"
              value={filterSender}
              onChange={(e) => setFilterSender(e.target.value)}
            />
            <Input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
            />
            <Input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button className="w-full sm:w-auto" size="sm" onClick={() => fetchLogs(1)}>Search</Button>
            <Button
              className="w-full sm:w-auto"
              size="sm"
              variant="outline"
              onClick={handleExportCsv}
              disabled={!logs.length}
            >
              <Download className="h-3 w-3 mr-1" />
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Document type</TableHead>
                  <TableHead>Sender email</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Abuse</TableHead>
                  <TableHead>Suspicious</TableHead>
                  <TableHead>TOS</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Sent at</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Acknowledged</TableHead>
                  <TableHead>Ack. Time</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Receipt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={16} className="text-center text-muted-foreground py-8">
                      No deliveries found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow
                      key={log.delivery_reference || log.reference}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleRowClick(log.delivery_reference || log.reference)}
                    >
                      <TableCell className="font-mono text-xs">{log.delivery_reference || log.reference}</TableCell>
                      <TableCell className="text-xs">{log.document_type}</TableCell>
                      <TableCell className="text-xs">{log.declared_sender_email || log.landlord_email || '—'}</TableCell>
                      <TableCell className="text-xs">{log.sender_ip || '—'}</TableCell>
                      <TableCell className="text-xs">{[log.ip_city, log.ip_country].filter(Boolean).join(', ') || '—'}</TableCell>
                      <TableCell className="text-xs">{log.abuse_score ?? '—'}</TableCell>
                      <TableCell className="text-xs">{log.is_suspicious ? 'Yes' : 'No'}</TableCell>
                      <TableCell className="text-xs">{log.tos_version_accepted ? `${log.tos_version_accepted} · ${log.tos_accepted_at ? new Date(log.tos_accepted_at).toLocaleString('en-GB') : ''}` : '—'}</TableCell>
                      <TableCell className="text-xs max-w-[150px] truncate">{log.property_address}</TableCell>
                      <TableCell className="text-xs">
                        {(log.recipients || []).map((r) => r.name).join(', ')}
                      </TableCell>
                      <TableCell className="text-xs">
                        {log.sent_at
                          ? new Date(log.sent_at).toLocaleDateString('en-GB')
                          : '—'}
                      </TableCell>
                      <TableCell>{statusBadge(log.delivery_status)}</TableCell>
                      <TableCell>
                        {(() => {
                          const total = (log.recipients || []).length;
                          const confirmed = (log.acknowledgements || []).length;
                          if (confirmed === 0) return <Badge variant="outline" className="text-gray-500">Pending</Badge>;
                          if (confirmed >= total) return <Badge className="bg-green-100 text-green-700 border-green-200">All confirmed</Badge>;
                          return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">{confirmed}/{total} confirmed</Badge>;
                        })()}
                      </TableCell>
                      <TableCell className="text-xs">
                        {(() => {
                          const acks = log.acknowledgements || [];
                          if (!acks.length) return '—';
                          const last = acks[acks.length - 1];
                          return new Date(last.acknowledged_at).toLocaleString('en-GB');
                        })()}
                      </TableCell>
                      <TableCell className="text-xs">{log.email_provider || '—'}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7 px-2"
                          onClick={(e) => handleDownloadReceipt(e, log.delivery_reference || log.reference)}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Receipt
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > LIMIT && (
        <div className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
          <span className="text-muted-foreground">{total} total</span>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button
              className="w-full sm:w-auto"
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => fetchLogs(page - 1)}
            >
              Previous
            </Button>
            <span className="px-2">Page {page}</span>
            <Button
              className="w-full sm:w-auto"
              size="sm"
              variant="outline"
              disabled={page * LIMIT >= total}
              onClick={() => fetchLogs(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog
        open={!!selectedLog || detailLoading}
        onOpenChange={() => { setSelectedLog(null); }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Delivery Detail — {selectedLog?.reference ?? '…'}
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex justify-center p-6">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : selectedLog && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-y-2 gap-x-2">
                {[
                  ['Reference', selectedLog.delivery_reference || selectedLog.reference],
                  ['Document type', selectedLog.document_type],
                  ['File', selectedLog.original_filename],
                  ['Status', selectedLog.delivery_status],
                  ['Property', selectedLog.property_address],
                  ['Sender', `${selectedLog.declared_sender_name || selectedLog.landlord_name || '—'} (${selectedLog.declared_sender_email || selectedLog.landlord_email || '—'})`],
                  ['Sent at', selectedLog.sent_at
                    ? new Date(selectedLog.sent_at).toLocaleString('en-GB', { timeZone: 'UTC' }) + ' UTC'
                    : '—'],
                  ['TOS accepted (version)', selectedLog.tos_version_accepted || '—'],
                  ['TOS accepted at', selectedLog.tos_accepted_at ? new Date(selectedLog.tos_accepted_at).toLocaleString('en-GB', { timeZone: 'UTC' }) + ' UTC' : '—'],
                  ['Hosted document', selectedLog.is_hosted_document ? 'Yes' : 'No'],
                  ['IP address', selectedLog.sender_ip || selectedLog.ip_address || '—'],
                  ['Approx. location', [selectedLog.ip_city, selectedLog.ip_country].filter(Boolean).join(', ') || '—'],
                  ['Abuse score', selectedLog.abuse_score ?? '—'],
                  ['Suspicious', selectedLog.is_suspicious ? 'Yes' : 'No'],
                  ['Rate-limit triggered', selectedLog.rate_limit_triggered ? 'Yes' : 'No'],
                  ['Bounce at', selectedLog.bounce_recorded_at
                    ? new Date(selectedLog.bounce_recorded_at).toLocaleString('en-GB', { timeZone: 'UTC' }) + ' UTC'
                    : '—'],
                ].map(([label, value]) => (
                  <div key={label} className="contents">
                    <span className="text-muted-foreground font-medium col-span-1">{label}</span>
                    <span className="col-span-2 break-all">{value}</span>
                  </div>
                ))}
              </div>

              <div>
                <p className="text-muted-foreground font-medium mb-2">Acknowledgements</p>
                <div className="space-y-2">
                  {(selectedLog.acknowledgements || []).map((ack, i) => (
                    <div key={i} className="border rounded p-2 text-xs space-y-0.5">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px]">Confirmed</Badge>
                        <span className="font-medium">{ack.name}</span>
                        <span className="text-muted-foreground">{ack.email}</span>
                      </div>
                      <div className="text-muted-foreground pl-0.5">
                        {new Date(ack.acknowledged_at).toLocaleString('en-GB', { timeZone: 'UTC' })} UTC
                        {ack.ack_ip && <> · IP: <span className="font-mono">{ack.ack_ip}</span></>}
                      </div>
                    </div>
                  ))}
                  {(selectedLog.recipients || []).filter(r => !(selectedLog.acknowledgements || []).some(a => a.email === r.email)).map(r => (
                    <PendingAckCard
                      key={r.email}
                      recipient={r}
                        reference={selectedLog.reference}
                      />
                    ))}
                  </div>
              </div>

              <div>
                <p className="text-muted-foreground font-medium mb-1">Recipients</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  {(selectedLog.recipients || []).map((r) => (
                    <li key={r.email}>{r.name} — {r.email}</li>
                  ))}
                </ul>
              </div>

              <div>
                <p className="text-muted-foreground font-medium mb-1">SHA-256 checksum</p>
                <p className="font-mono text-xs break-all bg-muted p-2 rounded">
                  {selectedLog.file_checksum || '—'}
                </p>
              </div>

              <div>
                <p className="text-muted-foreground font-medium mb-1">Covering message</p>
                <pre className="text-xs whitespace-pre-wrap bg-muted p-2 rounded">
                  {selectedLog.covering_message || '—'}
                </pre>
              </div>

              <div className="grid grid-cols-3 gap-y-2 gap-x-2">
                {/* Per-recipient tenant email IDs */}
                {(selectedLog.recipients && selectedLog.recipients.length > 0)
                  ? selectedLog.recipients.map((r, i) => (
                      <div key={`recipient-eid-${i}`} className="contents">
                        <span className="text-muted-foreground font-medium col-span-1">
                          {selectedLog.recipients.length === 1 ? 'Tenant email ID' : `Tenant ${i + 1} email ID`}
                        </span>
                        <span className="col-span-2 font-mono text-xs break-all">
                          {r.email_id || '—'}{r.email ? ` (${r.email})` : ''}
                        </span>
                      </div>
                    ))
                  : (
                    <div className="contents">
                      <span className="text-muted-foreground font-medium col-span-1">Tenant email ID</span>
                      <span className="col-span-2 font-mono text-xs break-all">
                        {selectedLog.resend_tenant_email_id || selectedLog.tenant_email_id || '—'}
                      </span>
                    </div>
                  )
                }
                {[
                  ['Email provider', selectedLog.email_provider ? selectedLog.email_provider.charAt(0).toUpperCase() + selectedLog.email_provider.slice(1) : '—'],
                  ['Landlord email ID', selectedLog.resend_landlord_email_id || selectedLog.landlord_email_id || '—'],
                  ['User agent', selectedLog.user_agent || '—'],
                ].map(([label, value]) => (
                  <div key={label} className="contents">
                    <span className="text-muted-foreground font-medium col-span-1">{label}</span>
                    <span className="col-span-2 font-mono text-xs break-all">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
